import { Button, CircularProgress, Select, Tooltip } from '@civilgrid/ui/components';
import { toKML } from '@placemarkio/tokml';
import { useMutation } from '@tanstack/react-query';
import type { Row, Table } from '@tanstack/react-table';
import { feature, featureCollection } from '@turf/turf';
import JSZip from 'jszip';
import { DateTime } from 'luxon';
import type React from 'react';
import { type ReactNode, useMemo } from 'react';
import type { useAsyncFn } from 'react-use';
import { useSnapshot } from 'valtio';
import { exportDrfTableToExcel as apiExportToExcel, regenerateApprovalPacket as apiRegeneratePacket } from '~/api/drf';
import { drfStatusLabels } from '~/common/drfUtils';
import { createExcelSheetDef, downloadExcelFileBuffer, type ExcelSheetDef } from '~/common/exportUtils';
import { DrfRequestStatus, type MapFulfillmentRequest } from '~/common/types';
import EmailLink from '~/components2/EmailLink';
import { SIBLING_AGENCY_MAP } from '~/components2/Map/Widgets/MapLayers';
import { Notify } from '~/components2/Notify';
import store from '~/store';

export function resetFormValues(r?: MapFulfillmentRequest) {
  return {
    name: r?.name || '',
    requestId: r?.requestId || '',
    client: r?.client || '',
    scope: r?.scope || '',
    startDate: r?.startDate,
    endDate: r?.endDate,
    requestStatus: r?.requestStatus || '',
    isPublicProject: r?.isPublicProject ?? false,
    publicProjectInfo: { ...(r?.publicProjectInfo || {}) },
    requestor: {
      name: r?.requestor?.name || '',
      email: r?.requestor?.email || '',
      phone: r?.requestor?.phone || '',
      organization: r?.requestor?.organization || '',
      address: {
        street: r?.requestor?.address?.street || '',
        city: r?.requestor?.address?.city || '',
        state: r?.requestor?.address?.state || '',
        zipCode: r?.requestor?.address?.zipCode || '',
      },
    },
  };
}

export function FormSectionTitle({ children }: { children?: React.ReactNode }) {
  return <div className="ml-1.5 text-lg text-secondary">{children}</div>;
}

export const skipNavColumns = new Set(['notes']);

const fulfilledStatuses = new Set([DrfRequestStatus.Fulfilled, DrfRequestStatus.AutoFulfilled]);

function getIsFulfilled(s: DrfRequestStatus) {
  return fulfilledStatuses.has(s);
}

interface StatusProps {
  update: ReturnType<typeof useAsyncFn>[0];
  fetchUpdate: (updates: Partial<MapFulfillmentRequest>) => Promise<MapFulfillmentRequest>;
  current: Row<MapFulfillmentRequest>;
  onNavigate: (to: 'next' | 'previous') => void;
}

export function StatusButtons({ update, fetchUpdate, current, onNavigate }: StatusProps) {
  const { requestStatus } = current.original;
  const isProcessing = update.loading;
  const hasError = !!update.error;
  const isFulfilled = getIsFulfilled(requestStatus);
  const isDisabled = isProcessing || hasError || isFulfilled;

  const regenerateApprovalPacketMutation = useMutation({
    mutationKey: ['drf-regenerate-approval-packet', { requestId: current.original.id }],
    mutationFn: () => apiRegeneratePacket(current.original.id),
    onError: (e) => {
      window.alert('An error occurred while attempting to generate the map facilities packet. Please contact support.');
    },
  });

  if (isFulfilled) {
    return (
      <div className="flex flex-col items-end">
        <div>This request has been fulfilled.</div>
        <div className="text-2xs">
          {regenerateApprovalPacketMutation.isPending ? (
            <CircularProgress indeterminate />
          ) : regenerateApprovalPacketMutation.isError ? (
            <>
              An error occurred while generating the map facilities packet. Please{' '}
              <EmailLink>contact support</EmailLink>.
            </>
          ) : regenerateApprovalPacketMutation.isSuccess ? (
            'Your map facilities packet is being generated. It will be sent to the requestor via email.'
          ) : (
            <>
              Click{' '}
              <span
                className="cursor-pointer text-brand hover:underline"
                onClick={() => {
                  regenerateApprovalPacketMutation.mutate();
                }}
              >
                here
              </span>{' '}
              to recreate the map facilities packet
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-x-2">
      <Button
        loading={isProcessing}
        disabled={isDisabled}
        onClick={async () => {
          const res = await fetchUpdate({
            requestStatus: DrfRequestStatus.Fulfilled,
          });
          if (getIsFulfilled(res.requestStatus)) {
            onNavigate('next');
          }
        }}
      >
        Approve
      </Button>

      <Button
        variant="secondary"
        loading={isProcessing}
        disabled={isDisabled}
        onClick={() => {
          fetchUpdate({ requestStatus: DrfRequestStatus.NeedsReview });
        }}
      >
        Needs Review
      </Button>
    </div>
  );
}

const statusItems = Object.values(DrfRequestStatus)
  .filter((s) => s !== DrfRequestStatus.AutoFulfilled)
  .map((s) => ({
    value: s,
    label: drfStatusLabels[s],
  }));

export function StatusSelect({ update, fetchUpdate, current, onNavigate }: StatusProps) {
  const { requestStatus } = current.original;
  const isProcessing = update.loading;
  const hasError = !!update.error;
  const isFulfilled = getIsFulfilled(requestStatus);
  const isDisabled = isProcessing || hasError || isFulfilled;

  const tooltip = useMemo<ReactNode>(() => {
    if (isFulfilled) {
      return 'This request has already been fulfilled';
    } else {
      return 'Set the request status manually';
    }
  }, [isFulfilled]);

  return (
    <Select
      label="Set Request Status Manually"
      disabled={isDisabled}
      value={requestStatus}
      options={statusItems}
      onValueChange={async (e) => {
        if (!e) return;
        const res = await fetchUpdate({
          requestStatus: e as DrfRequestStatus,
        });

        if (getIsFulfilled(res.requestStatus)) {
          onNavigate('next');
        }
      }}
      tooltipText={tooltip}
      data-testid="drf-request-status-select"
    />
  );
}

export function ExportTable({ table }: { table: Table<MapFulfillmentRequest> }) {
  const { currentOrganizationId } = useSnapshot(store);

  const {
    mutate: submitExport,
    isPending,
    isError,
  } = useMutation({
    mutationKey: ['drf-table-export-to-excel', { currentOrganizationId }],
    mutationFn: (data: ExcelSheetDef[]) =>
      apiExportToExcel(data).then((buffer) =>
        downloadExcelFileBuffer(
          buffer,
          `Utility-Map-Requests_${(store.currentOrganizationDetail as any)?.name}_${new Date().toLocaleString()}`,
        ),
      ),
    onError: (e) => {
      console.error('Error exporting to excel: ', e);
      Notify.error({
        message: 'An error occurred while exporting the table. Please contact support.',
      });
    },
  });

  const onExport = () => {
    const columns = table.getAllColumns();
    const rows = table.getCoreRowModel().rows;

    const createSheetByStatus = (status: DrfRequestStatus) => {
      return createExcelSheetDef({
        name: drfStatusLabels[status],
        columns,
        rows: rows.filter((row) => row.original.requestStatus === status),
      });
    };

    const allItems = createExcelSheetDef({
      name: 'All Map Requests',
      columns,
      rows,
    });

    const itemsByStatus = [DrfRequestStatus.Pending, DrfRequestStatus.PaymentPending, DrfRequestStatus.NeedsReview].map(
      createSheetByStatus,
    );

    const sheets = [allItems, ...itemsByStatus];
    submitExport(sheets);
  };

  return (
    <Button variant="secondary" loading={isPending || isError} disabled={isPending || isError} onClick={onExport}>
      Export to Excel
    </Button>
  );
}

export function ActivityFeedItem({ date, children }: { date: DateTime; children: React.ReactNode }) {
  return (
    <div className="relative flex w-full items-start justify-between">
      <div className="w-36 shrink-0">
        <Tooltip
          text={date.toLocaleString(DateTime.DATETIME_FULL)}
          data-testid={`activity-feed-date-${date.toMillis()}`}
        >
          {date.toLocaleString()}
        </Tooltip>
      </div>

      <div className="grow">{children}</div>
    </div>
  );
}

// duplicated in packages/api/src/common/drf.ts
export const CNP_ORG_IDS = ['1000004', '1000005', '1000081'];

// duplicated in packages/api/src/common/drf.ts
export function isCNPOrg(agencyId: string) {
  return CNP_ORG_IDS.includes(agencyId);
}

// duplicated in packages/jobs/src/processors/drf/common.ts
export const AGENCY_LAYER_CONFIG: { [orgAgencyId: string]: string[] } = {
  '43': ['gasDistribution', 'gasService', 'gasTransmission'],
  '8': [
    'electricDistributionLines',
    'electricDistributionServiceLines',
    'electricTransmissionLines',
    'electricDistributionOther',
  ],
  '1000004': [
    'cnpElectricTransmissionLines',
    'cnpElectricServiceLines',
    'cnpElectricDistributionLines',
    'cnpElectricStructures',
    'cnpElectricDevices',
  ],
  '1000005': [
    'cnpGasPoints',
    'cnpGasTransmission',
    'cnpGasService',
    'cnpGasDistribution',
    'cnpGasDimensionPoints',
    'cnpGasDimensionLines',
  ],
  '1000081': ['cnpFiberRoutes'],
};

// duplicated in packages/jobs/src/processors/drf/common.ts
export const CNP_METADATA_IDS = [
  // electric -> Temp removed until we have more data
  // 'cnpElectricDevices.type',
  // 'cnpElectricStructures.type',
  // 'cnpElectricDistributionLines.type',
  // 'cnpElectricDistributionLines.oh_ug',
  // 'cnpElectricDistributionLines.other_1',
  // 'cnpElectricTransmissionLines.subtype',
  // 'cnpElectricTransmissionLines.oh_ug',
  // 'cnpElectricTransmissionLines.other_1',
  // 'cnpElectricServiceLines.type',
  // 'cnpElectricServiceLines.oh_ug',
  'cnpElectricDistributionLines.ASSETTYPE_resolved',
  'cnpElectricTransmissionLines.ASSETTYPE_resolved',
  'cnpElectricServiceLines.ASSETTYPE_resolved',
  'cnpElectricDevices.LABELTEXT',
  'cnpElectricStructures.LABELTEXT',
  'cnpElectricDistributionLines.LABELTEXT',
  'cnpElectricTransmissionLines.LABELTEXT',
  'cnpElectricServiceLines.LABELTEXT',

  // gas
  'cnpGasService.diameter',
  'cnpGasService.material',
  'cnpGasDistribution.diameter',
  'cnpGasDistribution.material',
  'cnpGasTransmission.diameter',
  'cnpGasTransmission.material',
];

export function getLayerIdsForMultipleAgencies(orgAgencyId: string): string[] {
  return SIBLING_AGENCY_MAP[orgAgencyId]?.flatMap((agencyId) => AGENCY_LAYER_CONFIG[agencyId])?.filter(Boolean);
}

export function getMetadataIdsForAgenciesLayers(orgAgencyId: string): string[] {
  if (isCNPOrg(orgAgencyId)) {
    return CNP_METADATA_IDS;
  }

  return [];
}

export function BoundaryNote() {
  return (
    <div className="absolute left-4 top-4 rounded border bg-white p-1.5 text-xs">
      ⚠️ Requestor will receive data only within project boundary.
    </div>
  );
}

export function ExportGeometryToKMZButton({ request }: { request: MapFulfillmentRequest }) {
  const { mutate, isPending, isError } = useMutation({
    mutationKey: ['drf-export-geometry-to-kmz', { requestId: request.id }],
    onError: () => {
      Notify.error({
        message: 'An error occurred while exporting the geometry to KMZ.',
      });
    },
    mutationFn: async () => {
      const geometryFeature = feature(request.geometry);
      const fc = featureCollection([geometryFeature]);
      const kml = toKML(fc);
      const zip = new JSZip();
      zip.file('doc.kml', kml);
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${request.requestId}_geometry.kmz`;
      a.click();
      window.URL.revokeObjectURL(url);
      return null;
    },
  });

  return (
    <Button
      variant="secondary"
      onClick={() => {
        mutate();
      }}
      loading={isPending}
      disabled={isPending || isError}
    >
      Export Geometry to KMZ
    </Button>
  );
}
